import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { selectBorrowers } from 'store/loanApplications/selectors';
import { getTaskTypesAction } from 'store/system/actions';
import { selectTaskTypes } from 'store/system/selectors';
import { getConditionsAction } from 'store/underwriting/actions';
import { selectConditionsWithTaskTypes } from 'store/underwriting/selectors';

import { EMPTY_LIBRARY_CONDITIONS } from './constants';
import { buildConditionsByNumber, resolveLibraryConditions } from './utils';

// Resolves the id-only `add_conditions_from_library` entries into display-ready
// conditions. The AI-activity confirmation gate can be the first surface a
// reviewer opens, so the company condition library and task types are not
// guaranteed to be in the store yet — fetch them on demand (guarded) the same way
// the pending tool call itself is reloaded. Borrowers already live on the open
// loan application, so no fetch is needed for assignee names.
const useResolvedLibraryConditions = (libraryConditions = EMPTY_LIBRARY_CONDITIONS) => {
  const dispatch = useDispatch();
  const conditionsById = useSelector(selectConditionsWithTaskTypes);
  const taskTypesById = useSelector(selectTaskTypes);
  const borrowersById = useSelector(selectBorrowers);

  const hasLibraryConditions = libraryConditions.length > 0;
  const hasConditionsLoaded = !_.isEmpty(conditionsById);
  const hasTaskTypesLoaded = !_.isEmpty(taskTypesById);

  useEffectSafeStrictMode(() => {
    if (hasLibraryConditions && !hasConditionsLoaded) {
      dispatch(getConditionsAction());
    }
  }, [dispatch, hasLibraryConditions, hasConditionsLoaded]);

  useEffectSafeStrictMode(() => {
    if (hasLibraryConditions && !hasTaskTypesLoaded) {
      dispatch(getTaskTypesAction());
    }
  }, [dispatch, hasLibraryConditions, hasTaskTypesLoaded]);

  const conditionsByNumber = useMemo(
    () => buildConditionsByNumber(conditionsById),
    [conditionsById],
  );

  return useMemo(
    () =>
      resolveLibraryConditions({
        libraryConditions,
        conditionsById,
        conditionsByNumber,
        taskTypesById,
        borrowersById,
      }),
    [libraryConditions, conditionsById, conditionsByNumber, taskTypesById, borrowersById],
  );
};

export default useResolvedLibraryConditions;
